import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import Logo from './Logo';

const navigation = [
  { name: 'For candidates', href: '/for-candidates' },
  { name: 'For recruiters', href: '/for-recruiters' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Resources', href: '/resources' },
  { name: 'About', href: '/about' },
];

function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated } = useSupabaseAuth();
  const user = isAuthenticated ? true : null;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled
        ? 'dark:bg-black/20 dark:backdrop-blur-md bg-white/90 backdrop-blur-md shadow-sm'
        : 'dark:bg-black/20 dark:backdrop-blur-md bg-white/50 backdrop-blur-md'
        }`}
    >
      <nav className="flex items-center justify-between px-6 lg:px-8 py-3 max-w-7xl mx-auto" aria-label="Global">
        <div className="flex lg:flex-1">
          <Link to="/" className="group"><Logo size={28} /></Link>
        </div>
        <div className="flex lg:hidden">
          <button
            onClick={toggleTheme}
            className="mr-2 -m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-gray-300"
          >
            {theme === 'dark' ? (
              <SunIcon className="h-6 w-6" aria-hidden="true" />
            ) : (
              <MoonIcon className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 dark:text-gray-300 text-gray-700 dark:hover:text-white hover:text-gray-900"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-6 lg:items-center">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`text-sm font-medium leading-6 px-3 py-2 rounded-lg transition-all duration-200 whitespace-nowrap ${location.pathname === item.href
                ? 'dark:text-white dark:bg-transparent/10 text-blue-700 bg-blue-50'
                : 'dark:text-gray-300 dark:hover:text-white dark:hover:bg-transparent/5 text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
            >
              {item.name}
            </Link>
          ))}
          {user ? (
            <Link
              to="/dashboard"
              className="text-sm font-medium leading-6 px-4 py-2 rounded-full transition-all duration-200 text-white bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow whitespace-nowrap"
            >
              Dashboard
            </Link>
          ) : (
            <Link
              to="/login"
              className="text-sm font-medium leading-6 px-3 py-2 rounded-lg transition-all duration-200 dark:text-gray-300 dark:hover:text-white dark:hover:bg-transparent/5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 whitespace-nowrap"
            >
              Sign In
            </Link>
          )}
          <Link
            to="/register"
            className="text-sm whitespace-nowrap font-bold leading-6 text-white bg-blue-600 hover:bg-blue-700 px-6 py-2.5 rounded-full hover:shadow-lg hover:shadow-blue-500/20 transform hover:-translate-y-0.5 transition-all duration-200 border border-transparent"
          >
            Start Free Trial
          </Link>
          <button
            onClick={toggleTheme}
            className="ml-2 p-2 rounded-lg transition-colors duration-200 dark:text-gray-300 dark:hover:text-white text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-white/5"
            aria-label="Toggle Dark Mode"
          >
            {theme === 'dark' ? (
              <SunIcon className="h-5 w-5" />
            ) : (
              <MoonIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </nav>
      <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto dark:bg-slate-900 bg-white px-6 py-6 sm:max-w-sm sm:ring-1 dark:sm:ring-white/10 sm:ring-gray-200">
          <div className="flex items-center justify-between">
            <Link to="/"><Logo size={28} /></Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 dark:text-gray-300 text-gray-700 dark:hover:text-white hover:text-gray-900"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y dark:divide-gray-500/10 divide-gray-200">
              <div className="space-y-2 py-6">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 ${location.pathname === item.href
                      ? 'dark:text-white dark:bg-transparent/10 text-blue-700 bg-blue-50'
                      : 'dark:text-gray-300 dark:hover:text-white dark:hover:bg-transparent/5 text-gray-700 hover:bg-gray-50'
                      }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              <div className="py-6 space-y-3">
                <Link
                  to="/login"
                  className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 dark:text-white dark:hover:bg-transparent/5 text-gray-900 hover:bg-gray-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/contact"
                  className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 dark:text-white border dark:border-white/20 dark:hover:bg-transparent/5 text-gray-900 border-gray-200 hover:bg-gray-50 text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Request Demo
                </Link>
                <Link
                  to="/register"
                  className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-white bg-blue-600 hover:bg-blue-700 text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Start Free Trial
                </Link>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
}

export default Navbar;
